// aiSnapshotEnrichment — berikar workflow-snapshot med entity_details
// (description_md + attachment-filnamn) innan den skickas till
// ai-workflow-assistant edge-function.
//
// Användning: AiAssistantDialog.performSend kallar enrichSnapshot innan
// buildExtendContext så LLM:n ser per-entity-beskrivningar i extend-mode.
// Async pga supabase-fetch — caller awaitar.
//
// Datastruktur som injiceras i blocks/groups:
//   block.entity_details = {
//     description_md: string,
//     attachments: [{ filename, mime_type, size_bytes }]
//   }

import { supabase } from '@/integrations/supabase/client';
import type { MultiSheetWorkflowState, WorkflowState } from '@/types/workflow';
import { isMultiSheetState, wrapSingleSheet } from '@/types/workflow';

interface DetailRow {
  entity_id: string;
  entity_type: string;
  description_md: string | null;
  metadata: unknown;
  entity_attachments?: Array<{
    filename: string;
    mime_type: string;
    size_bytes: number;
  }>;
}

interface EnrichedDetails {
  description_md: string;
  attachments: Array<{ filename: string; mime_type: string; size_bytes: number }>;
}

async function fetchAllDetails(
  workflowId: string,
): Promise<Map<string, EnrichedDetails>> {
  const { data, error } = await supabase
    .from('entity_details')
    .select('entity_id, entity_type, description_md, metadata, entity_attachments(filename, mime_type, size_bytes)')
    .eq('workflow_id', workflowId);
  const out = new Map<string, EnrichedDetails>();
  if (error || !data) return out;
  for (const row of data as unknown as DetailRow[]) {
    const desc = row.description_md ?? '';
    const atts = row.entity_attachments ?? [];
    if (!desc.trim() && atts.length === 0) continue;
    out.set(row.entity_id, { description_md: desc, attachments: atts });
  }
  return out;
}

/**
 * Berikar snapshot med entity_details. Bevarar shape:n (single- vs
 * multi-sheet) och muterar inte input — returnerar ny kopia.
 *
 * När `workflowId` är null eller fetch failar returneras snapshoten
 * oförändrad. Designen är fail-soft: AI:n får ändå snapshot, bara utan
 * den extra entity-kontexten.
 */
export async function enrichSnapshotWithEntityDetails(
  snapshot: WorkflowState | MultiSheetWorkflowState | undefined,
  workflowId: string | null | undefined,
): Promise<WorkflowState | MultiSheetWorkflowState | undefined> {
  if (!snapshot || !workflowId) return snapshot;
  const details = await fetchAllDetails(workflowId);
  if (details.size === 0) return snapshot;

  const multi = isMultiSheetState(snapshot) ? snapshot : wrapSingleSheet(snapshot);
  const enriched: MultiSheetWorkflowState = {
    ...multi,
    sheets: multi.sheets.map((sheet) => ({
      ...sheet,
      blocks: sheet.blocks.map((b) => {
        const det = details.get(b.id);
        if (!det) return b;
        return { ...b, entity_details: det } as typeof b & { entity_details: EnrichedDetails };
      }),
      groups: sheet.groups.map((g) => {
        const det = details.get(g.id);
        if (!det) return g;
        return { ...g, entity_details: det } as typeof g & { entity_details: EnrichedDetails };
      }),
    })),
  };

  // Om input var single-sheet returnerar vi också single-sheet (extract
  // active sheet) så consumer-code som bygger UI inte påverkas.
  if (!isMultiSheetState(snapshot)) {
    const sheet = enriched.sheets[enriched.activeSheetIndex] ?? enriched.sheets[0];
    return {
      name: enriched.name,
      blocks: sheet.blocks,
      connectors: sheet.connectors,
      groups: sheet.groups,
      coordinateOrigin: enriched.coordinateOrigin,
    } as WorkflowState;
  }
  return enriched;
}
